/* eslint-disable max-len */
import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
gsap.registerPlugin( ScrollTrigger );

const sections = document.querySelectorAll( '.e-stn--focus-scroll' );
const MOBILE_MAX_WIDTH = 767;

const isMobileViewport = () => window.innerWidth <= MOBILE_MAX_WIDTH;

/**
 * Kill the desktop ScrollTrigger for the given section (keyed by id) and any
 * legacy per-item triggers that may still exist from previous runs.
 */
const killSectionTriggers = ( section, stickyContents ) => {
  const sectionId = section.dataset.focusScrollId;
  ScrollTrigger.getAll().forEach( ( trigger ) => {
    if ( ( sectionId && trigger.vars?.id === `focus-scroll-${ sectionId }` ) || stickyContents.includes( trigger.trigger ) ) {
      trigger.kill();
    }
  } );
};

/**
 * Mobile: accordion block often outputs `active` on every item and
 * `style="display:block"` on each body — that overrides CSS collapse.
 * Desktop scroll state is driven by ScrollTrigger; on mobile we only
 * keep the first panel open (unless `openIndex` is set for user clicks).
 */
const applyMobileAccordionState = ( section, stickyContents, medias, openIndex = 0 ) => {
  if ( ! isMobileViewport() ) {
    return;
  }

  killSectionTriggers( section, stickyContents );

  const items = section.querySelectorAll( '.e-acc__itm-wrpr' );
  items.forEach( ( item, i ) => {
    item.classList.remove( 'active-item', 'active' );
    const bodyWrpr = item.querySelector( '.e-acc__itm-bdy-wrpr' );
    bodyWrpr?.style.removeProperty( 'display' );
    item.querySelector( '.e-acc__itm-bdy' )?.style.removeProperty( 'min-height' );
    if ( i === openIndex ) {
      item.classList.add( 'active-item', 'active' );
      bodyWrpr?.style.setProperty( 'display', 'block' );
    }
  } );

  medias.forEach( ( media, i ) => {
    media.classList.toggle( 'active-media', i === openIndex );
  } );
};

const pauseVideos = ( section ) => {
  section.querySelectorAll( 'video' ).forEach(
    vid => {
      if ( !vid.paused ) {
        vid.pause();
        setTimeout( () => {
          vid.currentTime = 0;
        }, 600 );
      }
    }
  );
};

const setActiveIndex = ( section, items, medias, idx ) => {
  if ( isMobileViewport() ) {
    return;
  }
  const target = medias[ idx ];
  if ( target?.classList.contains( 'active-media' ) ) {
    return;
  }

  pauseVideos( section );
  medias.forEach( ( media, i ) => {
    media.classList.toggle( 'active-media', i === idx );
  } );
  items.forEach( ( item, i ) => {
    item.classList.toggle( 'active-item', i === idx );
  } );

  const hasVideo = medias[ idx ]?.querySelector( 'video' );
  hasVideo?.play();
};

/**
 * Determine which item should be active based on the current scroll
 * position. We pick the last item whose top has crossed the start line
 * (the vertical center of the sticky media wrapper).
 */
const computeActiveIndex = ( section, stickyContents ) => {
  const startY = updateStartPosition( section );
  let activeIdx = 0;
  stickyContents.forEach( ( el, i ) => {
    const rect = el.getBoundingClientRect();
    if ( rect.top <= startY ) {
      activeIdx = i;
    }
  } );
  return activeIdx;
};

const setupScrollAnimation = ( stickyContents, medias, section ) => {
  if ( isMobileViewport() ) {
    applyMobileAccordionState( section, stickyContents, medias, 0 );
    return;
  }

  ScrollTrigger.getAll().forEach( ( trigger ) => {
    if ( trigger.vars?.id === `focus-scroll-${ section.dataset.focusScrollId }` ) {
      trigger.kill();
    }
    if ( stickyContents.includes( trigger.trigger ) ) {
      trigger.kill();
    }
  } );

  const items = section.querySelectorAll( '.e-acc__itm-wrpr' );
  const itemsWrapper = section.querySelector( '.e-acc__itms-wrpr' );
  if ( ! itemsWrapper ) {
    return;
  }

  /**
   * One ScrollTrigger covers the full vertical range of all items.
   * On every scroll update we recompute which item should be active
   * from the current scroll position. This avoids relying on per-item
   * onEnter callbacks firing in order — which is what caused the
   * desync (off-by-one media) when scroll restoration, image loads,
   * or layout shifts left initial state out of sync.
   */
  ScrollTrigger.create( {
    id: `focus-scroll-${ section.dataset.focusScrollId }`,
    trigger: itemsWrapper,
    start: () => `top ${ updateStartPosition( section ) }`,
    end: () => `bottom ${ updateStartPosition( section ) }`,
    invalidateOnResize: true,
    onUpdate: () => {
      setActiveIndex( section, items, medias, computeActiveIndex( section, stickyContents ) );
    },
    onRefresh: () => {
      setActiveIndex( section, items, medias, computeActiveIndex( section, stickyContents ) );
    },
  } );

  setActiveIndex( section, items, medias, computeActiveIndex( section, stickyContents ) );
};

function handleSectionClick( section, accordion, medias ) {
  const accordionTabs = section.querySelectorAll( '.e-acc__itm-wrpr' );

  accordionTabs.forEach( ( slide ) => {
    slide.querySelectorAll( 'video' ).forEach(
      vid => {
        vid.pause();
        vid.currentTime = 0;
      } );

    if ( slide.classList.contains( 'active' ) || slide.classList.contains( 'active-item' ) ) {
      const video = slide.querySelector( 'video' );
      if ( video ) {
        video.play();
      }
    }
  } );

  /**
   * Capture on section runs before bubble handlers on the header / collapse
   * scripts, so one panel open + our classes win.
   */
  section.addEventListener( 'click', ( e ) => {
    const header = e.target.closest( '.e-acc__itm-hdr-wrpr' );
    if ( ! header || ! accordion.contains( header ) ) {
      return;
    }
    if ( ! isMobileViewport() ) {
      return;
    }
    e.preventDefault();
    e.stopPropagation();

    const slide = header.closest( '.e-acc__itm-wrpr' );
    if ( ! slide ) {
      return;
    }
    const slideIndex = [ ...accordionTabs ].indexOf( slide );
    if ( slideIndex < 0 ) {
      return;
    }

    accordionTabs.forEach( ( s ) => {
      s.classList.remove( 'active', 'active-item' );
      const bw = s.querySelector( '.e-acc__itm-bdy-wrpr' );
      const bd = s.querySelector( '.e-acc__itm-bdy' );
      if ( bw ) {
        bw.style.removeProperty( 'display' );
      }
      if ( bd ) {
        bd.style.removeProperty( 'min-height' );
      }
    } );

    slide.classList.add( 'active', 'active-item' );
    const bwOpen = slide.querySelector( '.e-acc__itm-bdy-wrpr' );
    if ( bwOpen ) {
      bwOpen.style.display = 'block';
    }

    medias.forEach( ( m, i ) => {
      m.classList.toggle( 'active-media', i === slideIndex );
    } );
    accordionTabs.forEach( ( item, i ) => {
      item.classList.toggle( 'active-item', i === slideIndex );
    } );

    section.querySelectorAll( 'video' ).forEach(
      vid => {
        vid.pause();
        vid.currentTime = 0;
      }
    );
    const currentVideo = slide.querySelector( 'video' );
    setTimeout( () => {
      if ( currentVideo && ( slide.classList.contains( 'active' ) || slide.classList.contains( 'active-item' ) ) ) {
        currentVideo.currentTime = 0;
        currentVideo.play();
      }
    }, 50 );
  }, true );
}

const updateStickyPosition = ( section ) => {
  const header = document.querySelector( 'header' );
  if ( ! header ) {
    return 0;
  }
  const hH = header.offsetHeight;
  const mediaWrapper = section.querySelector( '.e-acc__media-wrpr' );
  if ( ! mediaWrapper ) {
    return 0;
  }
  const mediaWrapperMarginTop = window.getComputedStyle( mediaWrapper ).marginTop;
  const topPosition = hH + parseFloat( mediaWrapperMarginTop );

  mediaWrapper.style.top = `${ topPosition }px`;

  return topPosition;
};

const updateStartPosition = ( section ) => {
  const stickyPosition = updateStickyPosition( section );
  const mediaWrapper = section.querySelector( '.e-acc__media-wrpr' );
  if ( ! mediaWrapper ) {
    return 0;
  }
  const mediaWrapperMinHeight = window.getComputedStyle( mediaWrapper ).minHeight;
  const startPosition = stickyPosition + ( parseFloat( mediaWrapperMinHeight ) / 2 );

  return startPosition;
};

/** Single mobile sync on load — avoid follow-up timers that reset after tap. */
const initMobileAccordionOnce = ( section, stickyContents, medias ) => {
  if ( ! isMobileViewport() ) {
    return;
  }
  applyMobileAccordionState( section, stickyContents, medias, 0 );
};

/**
 * Clone each item's media element into a shared wrapper and stamp
 * `item-N` / `media-N` classes. Returns the appended wrapper element.
 */
const buildMediaWrapper = ( accordion, accordionItems ) => {
  const wrapper = document.createElement( 'div' );
  wrapper.classList.add( 'e-acc__media-wrpr' );
  accordionItems.forEach( ( item, index ) => {
    item.classList.add( `item-${ index + 1 }` );
    const mediaElement = item.querySelector( '.e-div--media-wrapper' );
    if ( ! mediaElement ) {
      return;
    }
    const cloned = mediaElement.cloneNode( true );
    cloned.classList.add( `media-${ index + 1 }` );
    wrapper.appendChild( cloned );
    if ( index === 0 ) {
      item.classList.add( 'active-item' );
      cloned.classList.add( 'active-media' );
    }
  } );
  accordion.appendChild( wrapper );
  return wrapper;
};

/**
 * Create the ResizeObserver that keeps sticky position and scroll triggers
 * in sync. On mobile, avoid re-collapsing the accordion on every resize.
 */
const createResizeObserver = ( section, stickyContents, medias ) => {
  let lastWasMobile = isMobileViewport();
  let resizeRaf = 0;
  return new ResizeObserver( () => {
    cancelAnimationFrame( resizeRaf );
    resizeRaf = requestAnimationFrame( () => {
      resizeRaf = 0;
      updateStickyPosition( section );
      const mobile = isMobileViewport();
      if ( mobile ) {
        if ( ! lastWasMobile ) {
          applyMobileAccordionState( section, stickyContents, medias, 0 );
        }
        lastWasMobile = true;
        return;
      }
      lastWasMobile = false;
      setupScrollAnimation( stickyContents, medias, section );
    } );
  } );
};

let focusScrollIdCounter = 0;

const initSection = ( section ) => {
  const accordion =
    section.querySelector( '.e-acc--focus-scroll' ) ||
    section.querySelector( '.e-acc--focus-scroll-with-cta' );

  if ( ! accordion ) {
    return;
  }

  if ( accordion.dataset.focusScrollInit === '1' ) {
    return;
  }
  accordion.dataset.focusScrollInit = '1';
  section.dataset.focusScrollId = String( ++focusScrollIdCounter );

  // Let focus-scroll own open/close; block collapse / Bootstrap would fight us.
  accordion.querySelectorAll( '.e-acc__itm-hdr-wrpr' ).forEach( ( hdr ) => {
    hdr.removeAttribute( 'data-toggle' );
    hdr.removeAttribute( 'data-bs-toggle' );
    hdr.removeAttribute( 'data-target' );
    hdr.removeAttribute( 'data-bs-target' );
  } );

  /**
   * Guard against duplicate media wrappers when this script runs twice
   * (BFCache restore, partial hydration, etc.). Without this, two
   * `.e-acc__media-wrpr` would stack and the older one — stuck on its
   * previous active-media — would visually win on some browsers.
   */
  accordion.querySelectorAll( ':scope > .e-acc__media-wrpr' ).forEach( ( el ) => el.remove() );

  const stickyContents = gsap.utils.toArray( '.e-acc__itm-wrpr', accordion );
  const accordionItems = accordion.querySelectorAll( '.e-acc__itm-wrpr' );
  const accordionMediaWrapper = buildMediaWrapper( accordion, accordionItems );
  const medias = accordionMediaWrapper.querySelectorAll( '.e-div--media-wrapper' );

  const resizeObserver = createResizeObserver( section, stickyContents, medias );
  handleSectionClick( section, accordion, medias );
  resizeObserver.observe( section );
  initMobileAccordionOnce( section, stickyContents, medias );

  /**
   * Refresh once when the page is fully loaded — by then images/fonts
   * have settled and the start/end of our trigger reflect real layout.
   * `setActiveIndex` runs from `onRefresh`, so the visible media is
   * guaranteed to match the actual scroll position after this.
   */
  // Deferred via requestAnimationFrame so the refresh (and the forced
  // layout it triggers) runs as its own task instead of blocking the
  // 'load' handler itself, leaving room for queued input (e.g. a nav
  // click) to be processed first.
  const deferredRefresh = () => requestAnimationFrame( () => ScrollTrigger.refresh() );
  if ( document.readyState === 'complete' ) {
    deferredRefresh();
  } else {
    window.addEventListener( 'load', deferredRefresh, { once: true } );
  }
};

document.addEventListener( 'DOMContentLoaded', () => {
  if ( sections.length === 0 ) {
    return;
  }
  sections.forEach( initSection );
} );

const observer = new IntersectionObserver( ( entries ) => {
  entries.forEach( entry => {
    if ( entry.isIntersecting ) {
      entry.target.classList.add( 'in-viewport' );
    } else {
      entry.target.classList.remove( 'in-viewport' );
    }
  } );
}, {
  threshold: 0.15
} );

sections.forEach( section => {
  observer.observe( section );
} );
